import { ReactElement, useEffect, useMemo, useRef } from "react";
import { EventFilter } from "../../../../Events/Utilities/EventsApiService";
import { useQuery } from "react-query";
import moment from "moment";
import requirementApiService from "../../../../Requirements/Utilities/RequirementApiService";
import LoadingSpinner from "../../../../Shared/Components/LoadingSpinner";
import { RequirementAnalysis } from "../../../../../Models/Requirement/RequirementAnalysis";
import { rruleSummaryToText } from "../../../../Reporting/Utilities/ReportUtilities";
import { ConsumableReadDto } from "../../../../../Models/Consumable/ConsumableReadDto";
import consumablesApiService from "../../../../Consumables/Utilities/ConsumablesApiService";
import { ReactQueryKeys } from "../../../../../Constants/ClientRoutingConstants";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../../messages/en-US";
import NoRequirementPage from "../../NoRequirementPage";
import useChartStore from "../../../../../Zustand/chartStore";

import EndpointStatusChart from "../EndpointStatusChart";

import siteExplorerApiService from "../../../Utilities/SiteExplorerApiService";
import { ChartToolbar } from "../../../Utilities/ChartToolbar";


interface Props {
  eventFilter: EventFilter
  selectedItemId: string
  showTables: boolean
  isSimpleChart: boolean
  showChartTools?: boolean
  chartParentHeight?: number;
  greasePumpData?: {
    serialNo: string;
    channelNo: number;
    timestamps: number[];
    values: number[];
  };
  showLegendOverride?: boolean;
  showStatus?: boolean;
  /** When set (e.g. from dashboard), avoids refetch when switching tabs */
  queryStaleTime?: number;
  /** Reports whether there is a requirement to draw. */
  onHasData?: (hasData: boolean) => void;
}

export default function EndpointComplianceTabPage(props: Props): ReactElement {
  const localizationService = useLocalization();
  const viewType = useChartStore(state => state.viewType);
  const setViewType = useChartStore(state => state.setViewType);
  const setVisibleRange = useChartStore((state) => state.setVisibleRange);
  const setAutoRange = useChartStore((state) => state.setAutoRange);
  
  useEffect(() => {
    if (props.isSimpleChart) {
      setViewType('bar');
      setVisibleRange(moment(props.eventFilter.startDate).unix(), moment(props.eventFilter.endDate).unix());
      setAutoRange(true);
    }
  }, [props.isSimpleChart, setViewType, setVisibleRange, props.eventFilter, setAutoRange]);

  useEffect(() => {
    if(viewType !== 'userBar' && viewType !== 'userLine' && (viewType === 'table' || viewType === 'line')) {
      setViewType('bar');
    }
  }, [viewType, setViewType]);
  

  const queryOptions = props.queryStaleTime != null ? { staleTime: props.queryStaleTime } : {};

  const requirementsQuery = useQuery<RequirementAnalysis>([ReactQueryKeys.AnalysisRequirementQuery, props.selectedItemId, props.eventFilter], async () => {
    return requirementApiService.getAnalysisRequirement(props.selectedItemId, props.eventFilter);
  }, queryOptions);

  const consumablesQuery = useQuery<Array<ConsumableReadDto>>(ReactQueryKeys.AllConsumablesQuery, async () => {
    return consumablesApiService.getAllConsumables();
  }, queryOptions);

  const notificationsQuery = useQuery<any>(
    [ReactQueryKeys.AllNotificatiosStatusQuery, props.selectedItemId, props.eventFilter],
    async () => {
      return siteExplorerApiService.getEndpointNotifications(props.selectedItemId, props.eventFilter, 1);
    },
    queryOptions
  );

  const statusQuery = useQuery<any>(
    [ReactQueryKeys.AllStatusChartQuery, props.selectedItemId, props.eventFilter],
    async () => {
      return siteExplorerApiService.getEndpointStatus(props.selectedItemId, props.eventFilter, 0);
    },
    { ...queryOptions, enabled: !!props.selectedItemId }
  );

  const requirementResult = useMemo(() => {
    const consumableIdToName: { [id: string]: string } = {};
    consumablesQuery.data?.forEach((consumable) => {
      consumableIdToName[consumable.id] = consumable.name;
    });
    //TODO: This is a hack to get around the fact that the API returns a 200 with an empty array when there is no requirement.
    if(requirementsQuery.data?.requirementName !== "") {

    return{
        timestamps: requirementsQuery.data?.results.length === 0 ? requirementsQuery.data?.results : requirementsQuery.data?.results?.map(x => moment(x.start).unix()),
        volumeDispensedData: requirementsQuery.data?.results.length === 0 ? requirementsQuery.data?.results : requirementsQuery.data?.results?.map(item => item.volumeDispensed),
        volumeRequirement: requirementsQuery.data?.requirementDetails?.rightAmountDetails?.volumeRequirement,
        thresholds: requirementsQuery.data?.requirementDetails?.rightAmountDetails?.thresholds,
        summary: requirementsQuery.data?.requirementDetails?.rightTimeDetails?.rRule === undefined || requirementsQuery.data?.requirementDetails?.rightTimeDetails?.rRule === '' ? "No Requirement" : rruleSummaryToText(requirementsQuery.data?.requirementDetails?.rightTimeDetails?.rRule),
        consumableName: requirementsQuery.data?.requirementDetails.rightGreaseDetails?.consumableId === undefined ? '' : consumableIdToName[requirementsQuery.data?.requirementDetails.rightGreaseDetails?.consumableId],
        startDate: moment(requirementsQuery.data?.requirementDetails?.rightTimeDetails?.startDate).unix() ,
        endDate: moment(requirementsQuery.data?.requirementDetails?.rightTimeDetails?.endDate).unix() === moment(new Date('0001-01-01T00:00:00Z')).unix() ? moment(props.eventFilter.endDate).unix() : moment(requirementsQuery.data?.requirementDetails?.rightTimeDetails?.endDate).unix(),
        unplannedEventsTimestamps: requirementsQuery.data?.unplannedEvents.map(x => moment(x.timestamp).unix()),
        unplannedEventsVolumes: requirementsQuery.data?.unplannedEvents.map(x => x.volume),
        complianceWindowInMinutes: requirementsQuery.data?.requirementDetails?.rightTimeDetails?.complianceWindowInMinutes,
    }} else {
      return {
        timestamps: [],
        volumeDispensedData: [],
        volumeRequirement: 0,
        thresholds: [],
        summary: '',
        consumableName: '',
        startDate: moment().unix(),
        endDate: moment().unix(),
        unplannedEventsTimestamps: [],
        unplannedEventsVolumes: [],
        complianceWindowInMinutes: 0,
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[requirementsQuery.data])


  const isFirstMountRef = useRef(true);
  useEffect(() => {
    if (props.queryStaleTime != null && isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    isFirstMountRef.current = false;
    requirementsQuery.refetch();
    statusQuery.refetch();
    notificationsQuery.refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedItemId, props.eventFilter])

  const chartData = useMemo(() => {
    const rawLastSyncUnix = requirementsQuery.data?.lastSyncUtc ? moment(requirementsQuery.data.lastSyncUtc).unix() : 0;
    const results = requirementsQuery.data?.results;
    const lastResultEndUnix = results && results.length > 0 ? moment(results[results.length - 1].end).unix() : 0;
    const lastSyncUtcUnix = rawLastSyncUnix > 0 ? rawLastSyncUnix : lastResultEndUnix;
    const lastSeen = requirementsQuery.data?.lastSyncUtc === undefined ? localizationService.toLanguageString('custom.noEndpointDeviceAssigned', enMessages.custom.noEndpointDeviceAssigned) : lastSyncUtcUnix <= 0 ? localizationService.toLanguageString('custom.noEventsToDetermine', enMessages.custom.noEventsToDetermine) : moment.unix(lastSyncUtcUnix).format("DD/MM/YYYY @ HH:mm:ss");

    return {
      statusDataChart: {dateFilter: props.eventFilter, timestamps: requirementResult.timestamps, statusData:statusQuery.data,  alerts:notificationsQuery.data, },

              complianceDataChart: {dateFilter: props.eventFilter, strokeThickness: 1 , requirements:requirementResult,  lastSeen:moment(requirementsQuery.data?.lastSeen) ,lastSeenFormatted: lastSeen, lastSyncUtc: lastSyncUtcUnix, lastHeartbeatUtc:  moment(requirementsQuery.data?.lastHeartbeatUtc).unix(), complianceWindowInMinutes: requirementResult.complianceWindowInMinutes},

    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsQuery.data, statusQuery.data,requirementsQuery.data])

  const isLoadingCompliance = requirementsQuery.isLoading || requirementsQuery.isFetching || consumablesQuery.isLoading || consumablesQuery.isFetching || notificationsQuery.isLoading || notificationsQuery.isFetching || statusQuery.isLoading || statusQuery.isFetching;
  const hasNoRequirement = requirementsQuery.isError || requirementsQuery.data?.requirementName === "" || notificationsQuery.isError || statusQuery.isError || consumablesQuery.isError;

  const { onHasData } = props;
  // Treat "still loading" as having data, so the toolbar does not flicker.
  useEffect(() => {
    onHasData?.(isLoadingCompliance || !hasNoRequirement);
  }, [onHasData, isLoadingCompliance, hasNoRequirement]);

  if (isLoadingCompliance) {
    return <div className="flex items-center justify-center h-full w-full">
      <LoadingSpinner />
    </div>;
  } else {
    if (hasNoRequirement) {
      return  <div className="text-center"><NoRequirementPage /></div>;
    } else {
  return (
    <div>
       {!props.isSimpleChart && props.showChartTools !== false && (
      <ChartToolbar 
        title={localizationService.toLanguageString('custom.events', enMessages.custom.events)}
        lastSeen={chartData.complianceDataChart.lastSeenFormatted}
        showAutoRange={true} 
        showAggregatorOptions={false} 
        showChartOptions={true}
        showThresholds={true} 
        showSingleMultipleChart={false}
        showTable={false}
      />
      )}
      <EndpointStatusChart chartType="compliance" key={`${props.selectedItemId}PvAChart`} statusChartData={chartData.statusDataChart} complianceDataChart={chartData.complianceDataChart} isSimpleChart={props.isSimpleChart} chartParentHeight={props.chartParentHeight} greasePumpData={props.greasePumpData} showLegendOverride={props.showLegendOverride} showStatus={props.showStatus}/>
    </div>
  )
}}}